import { useState } from 'react';
import { assertSpecificSubject, destructivePhraseMatches, Size, t } from '@bms2/shared';
import { TextInput } from './TextInput';
import { ModalCancel, ModalShell } from '../modals/ModalShell';
import styles from './dialog.module.css';

/**
 * The Destructive Action Confirmation Dialog (piece 3, decision 0093). It is
 * not exported from the controls barrel: only a Button with color Destructive
 * opens it, hands it the action, and the action runs from inside it, so no
 * caller can build a destructive button that skips the gate. Confirmation is
 * a typed phrase, the confirm stays outlined until the phrase matches, and the
 * dialog names specifically what is being deleted.
 *
 * Since piece 5 it renders through the Modal shell, in the destructive tone:
 * the red header stays, and it is the one place a strong colour cue belongs
 * because every ordinary modal Primary is navy.
 */
export interface DestructiveConfirmationDialogProps {
  open: boolean;
  subject: string;
  phrase: string;
  onConfirm: () => void;
  onCancel: () => void;
  openerId?: string | null;
  /** 🔒 Decision 12970: a delete to the Trash says so, and its confirm reads Delete to the Trash; absent, the delete is permanent. */
  toTrash?: boolean;
}

export function DestructiveConfirmationDialog({ open, subject, phrase, onConfirm, onCancel, openerId = null, toTrash = false }: DestructiveConfirmationDialogProps) {
  assertSpecificSubject(subject);
  const [typed, setTyped] = useState('');
  const matches = destructivePhraseMatches(typed, phrase);

  // Every way out clears the typed phrase, so a reopened dialog asks again.
  // The reset lives on the paths rather than in an effect: an effect that sets
  // state on open renders the dialog twice for a field nobody has typed into.
  const cancel = () => {
    setTyped('');
    onCancel();
  };
  const confirm = () => {
    if (!matches) return;
    setTyped('');
    onConfirm();
  };

  return (
    <ModalShell
      open={open}
      title={t('dialog.destructive.title')}
      tone="destructive"
      onCancel={cancel}
      openerId={openerId}
      dataModal="destructive"
      footer={
        <>
          <ModalCancel label={t('dialog.cancel')} onCancel={cancel} />
          <button
            type="button"
            className={`${styles.confirm} ${matches ? styles.confirmFilled : ''}`}
            disabled={!matches}
            data-confirm={matches ? 'filled' : 'outlined'}
            onClick={confirm}
          >
            {t(toTrash ? 'dialog.destructive.confirmTrash' : 'dialog.destructive.confirm')}
          </button>
        </>
      }
    >
      <p data-subject={subject}>
        {t(toTrash ? 'dialog.destructive.bodyTrash' : 'dialog.destructive.body')} {subject}
      </p>
      <p>{t('dialog.destructive.instruction')}</p>
      <p className={styles.phrase} data-phrase={phrase}>
        <strong>{phrase}</strong>
      </p>
      <TextInput size={Size.Medium} label={t('dialog.destructive.phraseLabel')} value={typed} onChange={setTyped} autoComplete="off" />
    </ModalShell>
  );
}
